/**
 * Syncs the logged-in userId to the Flutter FCM service so push notifications
 * can be targeted to specific users.
 *
 * Call syncFcmUserId(userId) after a successful login, and once on app load
 * when the user is already known (e.g. from localStorage).
 *
 * This only has an effect inside the Flutter WebView — in a plain browser it
 * is a no-op.
 */

type FlutterWebView = {
  callHandler: (name: string, ...args: unknown[]) => Promise<unknown>;
};

function getFlutterWebView(): FlutterWebView | null {
  if (typeof window === 'undefined') return null;
  return (
    (window as unknown as { flutter_inappwebview?: FlutterWebView }).flutter_inappwebview ?? null
  );
}

/**
 * Notifies Flutter of the current userId so the FCM token is linked to the user.
 * Safe to call from anywhere — no-ops outside the Flutter WebView.
 */
export async function syncFcmUserId(userId: string): Promise<void> {
  if (!userId?.trim()) return;
  try {
    const bridge = getFlutterWebView();
    if (!bridge) return;
    await bridge.callHandler('syncFcmUserId', userId.trim());
  } catch {
    // Silently ignore — bridge may not be set up yet
  }
}

/**
 * Returns the current FCM token from Flutter, or null if not available.
 * Useful if the web app wants to store the token in its own database.
 */
export async function getFcmToken(): Promise<string | null> {
  try {
    const bridge = getFlutterWebView();
    if (!bridge) return null;
    const token = await bridge.callHandler('getFcmToken');
    return typeof token === 'string' ? token : null;
  } catch {
    return null;
  }
}
